// CASH1-S1-U1 and CASH1-S2-U1: the bills and coins grid of steppers, shared by the drawer screens (a counted float or close
// count) and the Cash screen at Pay (Other). One component so a fix reaches both. Money is never typed: every amount is a
// stepper count (cashDrawerModel.ts). All text via textContent. The caller owns the counts and redraws on change.

import {
  DENOMINATIONS,
  countOf,
  dollars,
  lineCents,
  stepCount,
  type DrawerCounts,
} from "./cashDrawerModel.ts";

export interface DenominationGridOptions {
  counts: DrawerCounts;
  /** Disables every stepper while a request is in flight. */
  busy: boolean;
  /** Prefix of every test id: `<prefix>-row-100`, `-minus-`, `-plus-`, `-qty-`, `-line-`, and `<prefix>-denominations`. */
  testPrefix: string;
  /** A stepper moved: the next counts. The caller stores them and draws again. */
  onChange: (next: DrawerCounts) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

function stepButton(
  className: string,
  text: string,
  testId: string,
  label: string,
  disabled: boolean,
  onClick: () => void,
): HTMLButtonElement {
  const node = el("button", className, text, testId);
  node.type = "button";
  node.setAttribute("aria-label", label);
  node.disabled = disabled;
  node.addEventListener("click", onClick);
  return node;
}

function column(
  options: DenominationGridOptions,
  title: string,
  kind: "bill" | "coin",
): HTMLElement {
  const wrap = el("div");
  wrap.append(el("h4", undefined, title));
  for (const denomination of DENOMINATIONS.filter((candidate) => candidate.kind === kind)) {
    const n = countOf(options.counts, denomination.cents);
    const row = el(
      "div",
      "cash-drow",
      undefined,
      `${options.testPrefix}-row-${denomination.cents}`,
    );
    const minus = stepButton(
      "cash-step",
      "−",
      `${options.testPrefix}-minus-${denomination.cents}`,
      `One less ${denomination.label}`,
      n === 0 || options.busy,
      () => options.onChange(stepCount(options.counts, denomination.cents, -1)),
    );
    const plus = stepButton(
      "cash-step plus",
      "+",
      `${options.testPrefix}-plus-${denomination.cents}`,
      `One more ${denomination.label}`,
      options.busy,
      () => options.onChange(stepCount(options.counts, denomination.cents, 1)),
    );
    row.append(
      el("span", "cash-dname", denomination.label),
      minus,
      el(
        "span",
        n === 0 ? "cash-qty zero" : "cash-qty",
        String(n),
        `${options.testPrefix}-qty-${denomination.cents}`,
      ),
      plus,
      el(
        "span",
        "cash-dline",
        n === 0 ? "" : dollars(lineCents(options.counts, denomination.cents)),
        `${options.testPrefix}-line-${denomination.cents}`,
      ),
    );
    wrap.append(row);
  }
  return wrap;
}

/** The two-column Bills and Coins grid. */
export function renderDenominationGrid(options: DenominationGridOptions): HTMLElement {
  const grid = el("div", "cash-denoms", undefined, `${options.testPrefix}-denominations`);
  grid.append(column(options, "Bills", "bill"), column(options, "Coins", "coin"));
  return grid;
}
